type MediaQueryChangeHandler = (event: MediaQueryListEvent) => void;

class MediaQueryStore {
  private mediaQueryByQuery = new Map<string, MediaQueryList>();

  private mediaQueryHandlersByQuery = new Map<
    string,
    Set<MediaQueryChangeHandler>
  >();

  private mediaQueryListenerByQuery = new Map<
    string,
    (e: MediaQueryListEvent) => void
  >();

  private cleanupMediaQuery(query: string) {
    this.removeMediaQueryListener(query);

    this.mediaQueryHandlersByQuery.delete(query);
    this.mediaQueryListenerByQuery.delete(query);
    this.mediaQueryByQuery.delete(query);
  }

  private removeMediaQueryListener(query: string) {
    const mediaQuery = this.mediaQueryByQuery.get(query);
    const listener = this.mediaQueryListenerByQuery.get(query);

    if (mediaQuery && listener) {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener("change", listener);
      } else {
        mediaQuery.removeListener(listener);
      }
    }
  }

  dispose() {
    this.mediaQueryByQuery.forEach((_, query) => {
      this.removeMediaQueryListener(query);
    });

    this.mediaQueryHandlersByQuery.clear();
    this.mediaQueryListenerByQuery.clear();
    this.mediaQueryByQuery.clear();
  }

  getMediaQuery(query: string) {
    return this.mediaQueryByQuery.get(query);
  }

  isMatch(query: string) {
    return !!this.getMediaQuery(query)?.matches;
  }

  registerMediaQueries(queries: string[]) {
    if (!queries?.length) {
      return;
    }

    queries.forEach((query) => {
      if (this.mediaQueryByQuery.has(query)) {
        return;
      }

      const mediaQuery = window.matchMedia(query);

      this.mediaQueryByQuery.set(query, mediaQuery);
      this.mediaQueryHandlersByQuery.set(query, new Set());

      const listener = (event: MediaQueryListEvent) => {
        const handlers = this.mediaQueryHandlersByQuery.get(query);

        handlers?.forEach((handler) => handler(event));
      };

      this.mediaQueryListenerByQuery.set(query, listener);

      if (mediaQuery.addEventListener) {
        mediaQuery.addEventListener("change", listener);
      } else {
        mediaQuery.addListener(listener);
      }
    });
  }

  subscribe(query: string, handler: MediaQueryChangeHandler) {
    if (!this.mediaQueryHandlersByQuery.has(query)) {
      this.registerMediaQueries([query]);
    }

    this.mediaQueryHandlersByQuery.get(query)?.add(handler);
  }

  unsubscribe(query: string, handler: MediaQueryChangeHandler) {
    const handlers = this.mediaQueryHandlersByQuery.get(query);

    if (!handlers || !handlers.has(handler)) {
      return;
    }

    handlers.delete(handler);

    if (handlers.size === 0) {
      this.cleanupMediaQuery(query);
    }
  }
}

const mediaQueryStore = new MediaQueryStore();

export default mediaQueryStore;
